/**
 * The Taxonomy screen's data hook. It loads the effective taxonomy (base plus
 * custom, each with its live document count) and the pending category
 * suggestions as one coordinated fetch, collapses them into a single
 * discriminated state, and exposes `reload()` so a mutation (create, toggle,
 * approve, reject, sweep) can pull the fresh truth back in.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/errors';
import type { CategorySuggestion, EffectiveCategory } from '../../api/schema';

import type { TaxonomyApi } from './api';

export interface TaxonomyData {
  categories: EffectiveCategory[];
  suggestions: CategorySuggestion[];
}

export type TaxonomyState =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | { status: 'ready'; data: TaxonomyData; reload: () => void };

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

/** True only for the AbortController-driven cancel of a superseded/unmounted load. */
function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * `canReviewSuggestions` gates the suggestions half of the load: a reader who
 * lacks `category_suggestions:review` (analyst, viewer) gets a 403 on
 * `GET /v1/category-suggestions`, and since both calls used to be awaited
 * together, that 403 failed the whole taxonomy view — including the
 * categories table, which they can read fine. Skipping the call entirely for
 * them means the categories table loads normally and suggestions default to
 * empty, rather than fabricating a page response for a call never made.
 */
export function useTaxonomy(api: TaxonomyApi, canReviewSuggestions: boolean): TaxonomyState {
  const [state, setState] = useState<TaxonomyState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const [categories, suggestions] = await Promise.all([
          api.listCategories(signal),
          canReviewSuggestions ? api.listSuggestions(signal) : Promise.resolve(null),
        ]);
        if (signal.aborted) return;
        setState({
          status: 'ready',
          reload,
          data: { categories: categories.items, suggestions: suggestions?.items ?? [] },
        });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [api, nonce, reload, canReviewSuggestions]);

  return state;
}
